import React, { useEffect, useState, useMemo } from 'react';
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button
} from 'reactstrap';
import { DateRangePicker } from 'react-date-range';
import { parse, startOfMonth, endOfMonth } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import withRouter from '../../components/Common/withRouter';
import TableContainer from '../../components/Common/TableContainer';
import Spinners from '../../components/Common/Spinner';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { useAuthStore } from '../../zustand-store/auth.store';
import { ToastContainer } from 'react-toastify';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import VerticalLayout from '../../components/VerticalLayout';
import { smsAndEmailReport } from '../../axios/appointment/index.api';
import * as XLSX from 'xlsx';

const SMSANDEMAILREPORT = () => {
  document.title = 'SMS Email Report | SeamlessSlot';

  const locationId = useAuthStore((state) => state.locationId);
  const businessId = useAuthStore((state) => state.businessId);
  const [pageSize, setPageSize] = useState(100);

  const ContactsProperties = createSelector(
    (state) => state.contacts,
    (Contacts) => ({
      users: Contacts?.users,
      loading: Contacts?.loading
    })
  );

  const { loading } = useSelector(ContactsProperties);
  const [isLoading, setLoading] = useState(loading);
  const [data, setData] = useState();
  const [filteredData, setFilteredData] = useState([]);
  const [selectedMonthYear, setSelectedMonthYear] = useState('This Month');
  const [monthYearOptions, setMonthYearOptions] = useState([]);

  // Modal & Date Range State
  const [modalOpen, setModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection'
    }
  ]);

  useEffect(() => {
    localStorage.setItem('path', '/reports/smsemailreport');
    getData();
  }, [locationId]);

  const getData = async () => {
    setLoading(true);
    let res = await smsAndEmailReport(parseInt(businessId));
    const report = res?.data?.data?.smsAndEmailReport || [];

    const uniqueMonthYears = [...new Set(report.map((item) => item.monthYear))];
    setMonthYearOptions(['This Month', 'All', ...uniqueMonthYears]);

    setData(res);
    setFilteredData(filterByMonthYear(report, 'This Month'));
    setLoading(false);
  };

  const filterByMonthYear = (data, selected) => {
    if (selected === 'All') return data;

    if (selected === 'This Month') {
      const now = new Date();
      const currentMonthYear = `${now.toLocaleString('default', {
        month: 'short'
      }).slice(0, 3)}, ${now.getFullYear()}`;
      return data.filter((item) => item.monthYear === currentMonthYear);
    }

    return data.filter((item) => item.monthYear === selected);
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setSelectedMonthYear(value);

    const reportData = data?.data?.data?.smsAndEmailReport || [];
    setFilteredData(filterByMonthYear(reportData, value));
  };

  const handleDateFilter = () => {
    const reportData = data?.data?.data?.smsAndEmailReport || [];

    const start = startOfMonth(dateRange[0].startDate);
    const end = endOfMonth(dateRange[0].endDate);

    const filtered = reportData.filter((item) => {
      try {
        const itemDate = parse(item.monthYear, 'MMM, yyyy', new Date());
        console.log(itemDate, start, end);
        return itemDate >= start && itemDate <= end;
      } catch (err) {
        console.error('Date parse error for item:', item, err);
        return false;
      }
    });

    console.log('Filtered:', filtered);
    setFilteredData(filtered);
    setModalOpen(false);
  };

  const columns = useMemo(
    () => [
      {
        header: 'Location',
        accessorKey: 'locationName',
        enableColumnFilter: false,
        enableSorting: true
      },
      {
        header: 'Month Year',
        accessorKey: 'monthYear',
        enableColumnFilter: false,
        enableSorting: true
      },
      {
        header: 'Total Emails',
        accessorKey: 'totalEmails',
        enableColumnFilter: false,
        enableSorting: true
      },
      {
        header: 'Total SMS',
        accessorKey: 'totalSms',
        enableColumnFilter: false,
        enableSorting: true
      },
      {
        header: 'Total Email Cost',
        accessorKey: 'totalEmailCost',
        enableColumnFilter: false,
        enableSorting: true
      },
      {
        header: 'Total SMS Cost',
        accessorKey: 'totalSmsCost',
        enableColumnFilter: false,
        enableSorting: true
      },
      {
        header: 'Total Price',
        accessorKey: 'totalPrice',
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const emailCost = parseFloat(row.original.totalEmailCost?.split('$')[1]) || 0;
          const smsCost = parseFloat(row.original.totalSmsCost?.split('$')[1]) || 0;
          const total = (emailCost + smsCost).toFixed(2);
          return `$${total}`;
        }
      }
    ],
    [businessId]
  );

  const handleExport = () => {
    const reportData = filteredData || [];

    if (reportData.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(
      reportData.map((item) => ({
        Location: item.locationName,
        'Month Year': item.monthYear,
        'Total Emails': item.totalEmails,
        'Total SMS': item.totalSms,
        'Total Email Cost': item.totalEmailCost,
        'Total SMS Cost': item.totalSmsCost,
        'Total Price': `$${(
          parseFloat(item.totalEmailCost?.split('$')[1]) ||
          0 + parseFloat(item.totalSmsCost?.split('$')[1]) ||
          0
        ).toFixed(2)}`
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'SMS & Email Report');
    XLSX.writeFile(workbook, 'SMS_Email_Report.xlsx');
  };

  return (
    <VerticalLayout>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Sms and Email Report" breadcrumbItem="Sms and Email Report" />
          {isLoading ? (
            <Spinners setLoading={setLoading} />
          ) : (
            <Row>
              <Col lg="12">
                <Card>
                  <CardBody>
                    <div className="d-flex justify-content-between mb-3">
                      <div className="d-flex gap-2">
                        <select
                          className="form-select"
                          value={selectedMonthYear}
                          onChange={handleFilterChange}
                          style={{ width: '200px' }}>
                          {monthYearOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <Button color="primary" onClick={() => setModalOpen(true)}>
                          <i className="bx bx-filter text-light" style={{ fontSize: 20 }} />
                        </Button>
                      </div>
                      <Button color="primary" onClick={handleExport}>
                        Export to Excel
                      </Button>
                    </div>
                    <TableContainer
                      columns={columns}
                      data={filteredData}
                      isPagination={false}
                      isServerPagination={true}
                      allowFilters={false}
                      isGlobalFilter={false}
                      SearchPlaceholder="Search..."
                      isCustomPageSize={true}
                      buttonClass="btn bg-primary rounded-pill addContact-modal mb-2 px-4"
                      tableClass="align-middle table-nowrap table-hover dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination"
                      pageSizeOptions={[100, 200]}
                      pageSize={pageSize}
                      setPageSize={setPageSize}
                      tourId="open-add-service-modal"
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>

      {/* Date Range Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} size="xl">
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>Filter by Date Range</ModalHeader>
        <ModalBody>
          <DateRangePicker
            editableDateInputs={true}
            onChange={(item) => setDateRange([item.selection])}
            moveRangeOnFirstSelection={false}
            ranges={dateRange}
            months={2}
            direction="horizontal"
            rangeColors={['#000']}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleDateFilter}>
            Apply
          </Button>{' '}
          <Button color="secondary" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      <ToastContainer />
    </VerticalLayout>
  );
};

export default withRouter(SMSANDEMAILREPORT);
